/**
 * Industries hub + sector page copy (v3, 23 Sep 2026).
 *
 * Source: "Industries — approved copy for implementation"
 * (Pending Updates/Industries-20260923-v3, APPROVED-COPY.md), approved by
 * Gary Cameron. Copy is transcribed verbatim; apostrophes are normalised to
 * the site's straight-quote convention, document anchors (#pubs) become the
 * stated routes (/industries/pubs) and absolute candopayments.com links
 * become relative paths. Section order is fixed by the approved copy —
 * change it there first, then here.
 *
 * Claims (64% saving, 30-day to 18-month contracts, 98% callbacks, named
 * account manager) carry their qualifications inline. Named customer
 * examples are CanDo-authored text covered by recorded naming permission
 * (22 Sep 2026); they are not quotes or ratings. Sectors without a named
 * example keep the neutral review link only — do not add star scores,
 * review counts, testimonials or another sector's examples here.
 *
 * Inline links use `[label](/path)` and are rendered by <InlineCopy />; FAQ
 * schema receives plain text so JSON-LD matches the visible answer.
 *
 * Page `sections` are rendered in order by IndustryPage:
 *   cards    — H2 with H3 cards (body is a string or array of paragraphs)
 *   prose    — H2 with paragraphs
 *   examples — H2 with named customer cards (+ optional note)
 */

export { plainText } from "@/data/locationsContent";

export const CTA = {
  statement: { label: "Get a free statement review", to: "/statement-review" },
  inPerson: { label: "Estimate in-person payment costs", to: "/quote/in-person" },
  online: { label: "Taking payments online? Estimate online costs", to: "/quote/online" },
  onlineFirst: { label: "Estimate online payment costs", to: "/quote/online" },
  inPersonSecond: { label: "Also selling face to face? Estimate in-person costs", to: "/quote/in-person" },
};

const EXISTING_CARD = {
  heading: "Already taking card payments?",
  body:
    "Have a recent merchant statement ready. We'll check your rates, contract and settlement details, explain what you're paying, and show you what could change. Where eligible, we'll match or beat your current payment rate.",
  cta: CTA.statement,
  support: "The form also asks for your business and contact details.",
};

export const CHOICE_BLOCK = {
  eyebrow: "Start in the right place",
  heading: "Already taking payments, or starting from scratch?",
  existing: EXISTING_CARD,
  newBusiness: {
    heading: "Setting up a new business?",
    body:
      "You do not need a merchant statement. Start with an estimate for the way you expect customers to pay, then ask us for a quote tailored to your business.",
    cta: CTA.inPerson,
    link: CTA.online,
    support: "Estimates are indicative, not formal quotes.",
  },
};

// E-commerce leads with the online estimate (handover: "E-commerce uses the
// online estimate first").
export const ECOM_CHOICE_BLOCK = {
  ...CHOICE_BLOCK,
  newBusiness: {
    heading: "Setting up an online business?",
    body:
      "You do not need a merchant statement. Start with an estimate for online payments, then talk to us about your website, checkout and a quote tailored to your business.",
    cta: CTA.onlineFirst,
    link: CTA.inPersonSecond,
    support: "Estimates are indicative, not formal quotes.",
  },
};

export const PROOF_STRIP = {
  items: [
    "Save up to 64% on payment processing",
    "Terminal contracts from 30 days",
    "98% of callbacks returned within two business hours",
  ],
  note: "Savings depend on your current setup and eligibility. Contract options run from 30 days to 18 months.",
};

export const ECOM_PROOF_STRIP = {
  items: [
    "Save up to 64% on payment processing",
    "A named account manager",
    "98% of callbacks returned within two business hours",
  ],
  note: "Savings depend on your current setup and eligibility.",
};

export const SUPPORT_BLOCK = {
  eyebrow: "Help with your setup",
  heading: "A named person to talk to",
  body:
    "We're based in Ellesmere Port, and you'll have a named account manager. Tell us how your business takes payments, what needs to connect and where your current setup gets in the way. We'll help you compare the options and explain the proposed setup before you decide.",
};

export const REVIEW_BLOCK = {
  eyebrow: "Customer experiences",
  heading: "What business owners say about working with us",
  body: "Read about customers' experiences of our setup help and ongoing support.",
  cta: { label: "Read customer reviews", to: "/reviews" },
};

export const FORM_INTRO = "Tell us what you need to take payments for and what you would like to improve.";

export const FINAL_CTA = {
  heading: "Ready to look at your payment setup?",
  body:
    "Already taking payments? Start with a free statement review. Opening a new business? Explore payment costs, then talk to us about the equipment and services you need.",
  primary: CTA.statement,
  secondary: CTA.inPerson,
  link: CTA.online,
};

export const ECOM_FINAL_CTA = {
  heading: "Ready to look at your payment setup?",
  body:
    "Already taking payments? Start with a free statement review. Opening an online business? Explore payment costs, then talk to us about the equipment and services you need.",
  primary: CTA.statement,
  secondary: CTA.onlineFirst,
  link: CTA.inPersonSecond,
};

/* ─── /industries hub ──────────────────────────────────────────────────── */

export const HUB = {
  path: "/industries",
  seoTitle: "Card Payments & EPOS by Industry | CanDo Payments",
  metaDescription:
    "Find card machines, EPOS and online payment options for your industry. Practical guidance for hospitality, retail, salons, fitness and other UK businesses.",
  eyebrow: "Payments for your industry",
  h1: "Card payment solutions for your industry",
  heroBody:
    "A café queue, a salon appointment and an online order each need a different payment setup. We help you choose card machines, EPOS and online payment services around how you serve customers. Find your industry below for the questions to ask, the options to consider and a useful place to start.",
  finder: {
    heading: "Find your industry",
    intro:
      "Choose the page that best fits your business. If you do several things, start with the part of the business where payments need the most attention.",
    groups: [
      {
        heading: "Food, drink and hospitality",
        items: [
          { slug: "hospitality", label: "Restaurants, hotels and hospitality", body: "Taking payments at the table, arranging event deposits and handling accommodation payments." },
          { slug: "pubs", label: "Pubs and bars", body: "Counter and table payments, busy service periods, tabs and the checks to make before choosing EPOS." },
          { slug: "cafes", label: "Cafés and coffee shops", body: "Counter service, food orders and payment costs when many of your sales are small." },
          { slug: "takeaways", label: "Takeaways and food-to-go", body: "Walk-in, phone and online orders, with a clear plan for payment and collection." },
        ],
      },
      {
        heading: "Shops and online businesses",
        items: [
          { slug: "retail", label: "Retail shops", body: "Card machines and EPOS for the till, stockroom and businesses selling across more than one channel." },
          { slug: "e-commerce", label: "E-commerce", body: "Online checkout, payment gateways and the practical checks involved in changing payment provider." },
        ],
      },
      {
        heading: "Appointments, memberships and services",
        items: [
          { slug: "beauty", label: "Beauty and hair", body: "Salon checkout, booking deposits, product sales and payments for independent professionals." },
          { slug: "personal-care", label: "Personal care and appointment services", body: "Reception, advance payments and mobile working for clinics, therapists and appointment-led businesses." },
          { slug: "fitness", label: "Gyms and fitness businesses", body: "Memberships, classes, personal training and everyday sales at reception." },
          { slug: "pet-services", label: "Pet services", body: "Payments for grooming, boarding, veterinary practices, pet shops and visits to customers." },
        ],
      },
    ],
  },
  options: {
    heading: "Choose card machines, EPOS or online payments",
    items: [
      {
        heading: "In person",
        body:
          "If customers pay at a fixed till, start with a countertop machine. If your team takes payments at tables or away from the counter, look at portable or mobile options and the connectivity they need. Explore our [card machines](/card-machines).",
      },
      {
        heading: "Alongside a till or booking process",
        body:
          "An [EPOS system](/epos-systems) can bring sales, ordering and reporting into the same working process. The useful question is which jobs you need it to do: take orders, manage stock, record services or connect with other software. We'll discuss the specific system and features before recommending a setup.",
      },
      {
        heading: "Online or before a visit",
        body:
          "Website checkout, [online ordering](/online-ordering) and payment links solve different problems. A customer buying online needs a checkout; a customer paying a deposit may only need a payment link. Our [online payment options](/online-payments) are a place to compare those routes.",
      },
      {
        heading: "Over the phone",
        body:
          "If customers place orders or pay invoices by phone, ask about a [virtual terminal](/virtual-terminal). We'll discuss the intended use, access and staff process as part of choosing a suitable setup.",
      },
    ],
  },
  comparing: {
    heading: "Comparing payment providers for your business",
    paragraphs: [
      "Start with the jobs the service needs to do, then compare the full cost: payment processing, equipment, software and any connections. Bring a recent statement if you already take cards. For a new business, tell us your expected sales and how customers will pay so the proposal reflects your plans.",
    ],
  },
  moreThanOne: {
    heading: "More than one kind of business?",
    paragraphs: [
      "A farm shop with a café, a gym with a retail counter or a salon selling online does not fit neatly into one category. Tell us where each payment starts, where it is completed and which systems your team uses. We can then discuss the separate requirements together.",
      "If your industry is not listed, [tell us about your business](/contact). We'll discuss your activity and requirements before confirming whether we can help.",
    ],
  },
  faqHeading: "Questions about choosing payment services",
  faqs: [
    {
      q: "Do I need a different card machine for each industry?",
      a: "Not necessarily. Where customers pay, the connection available and any till integration are more useful starting points than an industry label. Your business activity also forms part of the application and suitability checks.",
    },
    {
      q: "Can I get help if I am opening my first business?",
      a: "Yes. You can start with an in-person or online cost estimate without an existing merchant statement. We'll need to understand your plans before providing a tailored quote.",
    },
    {
      q: "Can you review the provider I already use?",
      a: "Yes. A free statement review gives us a starting point for discussing your rates and current setup. Tell us about any equipment, software or contract commitments that also need to be considered.",
    },
    {
      q: "Does every EPOS system include every feature shown on these pages?",
      a: "No. Features, connections and costs depend on the selected system and package. Tell us which requirements are essential so they can be checked before you decide.",
    },
    {
      q: "Can I take both online and face-to-face payments?",
      a: "We can discuss both. Whether they can share reporting or connect to the same stock or booking system depends on the products and integrations selected.",
    },
  ],
};

/* ─── Sector pages ─────────────────────────────────────────────────────── */

const ALL_INDUSTRIES = { label: "All industries", to: "/industries" };

export const INDUSTRIES = {
  hospitality: {
    breadcrumb: "Hospitality",
    seoTitle: "Hospitality Payment Solutions & EPOS | CanDo Payments",
    metaDescription:
      "Card machines, EPOS and remote payments for restaurants, hotels and hospitality venues. Explore table payments, deposits and practical setup support.",
    eyebrow: "Restaurants, hotels and hospitality",
    h1: "Hospitality payment solutions for restaurants and hotels",
    heroBody:
      "The bill at the end of a meal, a wedding deposit and payment for a room all need a clear process. We help restaurants, hotels and hospitality venues choose card machines, EPOS and remote payment options around the way their teams work—from the first booking to the final bill.",
    img: "https://cdn.candopayments.com/industries/hospitality_colour_web_solid.webp",
    sections: [
      {
        type: "cards",
        heading: "Make payments part of the service",
        items: [
          {
            heading: "Take the payment to the table",
            body:
              "If staff move between tables, a portable terminal may suit service better than a machine fixed at reception. Think about the areas it needs to reach, where it will charge and what happens when several tables want to pay at once. We'll discuss the connection and equipment for your venue.",
          },
          {
            heading: "Choosing a hospitality POS system",
            body:
              "A point-of-sale system, often called POS or EPOS, records orders and sales; a card machine handles the payment. For a restaurant, compare how those parts work together during service. Check the software, equipment and connection costs as well as the card-processing rate.",
          },
          {
            heading: "Follow the order through to the bill",
            body:
              "Taking an order, sending it to the kitchen and settling the bill are connected jobs. An [EPOS system](/epos-systems) should be assessed against that whole process. If you need kitchen displays, table management or bill splitting, ask to see those exact features working with the proposed payment setup before choosing it.",
          },
          {
            heading: "Arrange deposits and remote payments",
            body:
              "Functions, private dining and accommodation can involve payment before the customer arrives. A payment link gives the customer a way to pay remotely. Tell us how you record the booking, the amount due and any balance so we can discuss a suitable [online payment process](/online-payments).",
          },
        ],
      },
      {
        type: "prose",
        heading: "Restaurant card machines and hotel payment requirements",
        paragraphs: [
          "For restaurants, start with table service, bill splitting and the connection to your till. Hotels may also need advance payments, pre-authorisations and a booking-system connection. Tell us which journeys matter at your venue; we'll check the relevant equipment, software and account requirements together.",
        ],
      },
      {
        type: "examples",
        heading: "Experience across hospitality",
        items: [
          {
            name: "The Coachhouse",
            body: "We supplied The Coachhouse with iPad-based EPOS, card machines and online payments. Payment links also help the team arrange wedding deposits.",
          },
          {
            name: "Sevenstay Aparthotels",
            body: "We helped Sevenstay Aparthotels take accommodation payments directly through payment links and pre-authorisations, giving the team an alternative to processing payments through a booking platform.",
          },
          {
            name: "Porta Tapas",
            body: "We support Porta Tapas and related sites with card payments and local support. Bill-splitting software on the terminals helps staff handle customers who want to pay separately.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Choosing hospitality merchant services",
        paragraphs: [
          "Before comparing equipment, walk through a typical service: who takes the order, where the bill is checked, who can make a correction and where payment is recorded. Include a busy period and an awkward situation, such as a part-paid booking or a customer asking to split a bill.",
          "Bring your current till and booking-system details to the conversation. A useful proposal should explain what can be retained, what needs to change, any separate software charges and the support arrangements. For hotels, pre-authorisation and booking-system connections need specific confirmation; they are not included with every terminal or gateway.",
          "If you mainly run a [pub or bar](/industries/pubs), [café](/industries/cafes) or [takeaway](/industries/takeaways), our dedicated pages cover those service patterns in more detail.",
        ],
      },
    ],
    faqHeading: "Hospitality payment questions",
    faqs: [
      {
        q: "Can customers pay at their table?",
        a: "Portable terminals can support payment away from a fixed counter. The right connection depends on your premises. If you also want the bill sent directly from the till, we'll need to check compatibility with your EPOS.",
      },
      {
        q: "Can I take a deposit without putting a full checkout on my website?",
        a: "Payment links are one option for remote deposits. Tell us how you manage bookings and remaining balances so the payment process fits the way your team works.",
      },
      {
        q: "Can you work with our existing restaurant till?",
        a: "We'll need its name, version and payment-integration details before confirming that. An existing till does not automatically need replacing, but compatibility should be checked before equipment is ordered.",
      },
      {
        q: "Do all hotel payments work like a restaurant payment?",
        a: "No. Accommodation may involve advance payments, pre-authorisations or a booking-system connection. Explain the journeys you need so the relevant product support and account requirements can be checked.",
      },
      {
        q: "What should I include when asking for a quote?",
        a: "Tell us your number of sites, tills and service areas, how customers book and pay, and the systems you already use. If you take card payments today, a recent merchant statement helps us compare the processing costs.",
      },
    ],
    related: [
      { label: "Card machines", to: "/card-machines" },
      { label: "EPOS systems", to: "/epos-systems" },
      { label: "Online ordering", to: "/online-ordering" },
      ALL_INDUSTRIES,
    ],
    formHeading: "Talk to us about your hospitality payment setup",
  },

  pubs: {
    breadcrumb: "Pubs & Bars",
    seoTitle: "Card Machines & EPOS for Pubs and Bars | CanDo Payments",
    metaDescription:
      "Compare card machines and EPOS for pubs, bars and live venues. Plan counter payments, table service and busy shifts with practical help from CanDo.",
    eyebrow: "Pubs, bars and live venues",
    h1: "Card machines and EPOS for pubs and bars",
    heroBody:
      "A round at the bar, food at a table and a busy event can put different demands on the same payment setup. We help pubs, bars and live venues choose card machines and EPOS around their service areas, staff and busiest shifts.",
    img: "https://cdn.candopayments.com/industries/pubs-and-bars_colour_web_solid.webp",
    sections: [
      {
        type: "cards",
        heading: "Build the setup around your bar",
        items: [
          {
            heading: "Enough payment points for the places you serve",
            body:
              "Count where customers actually pay: the main bar, a second room, the beer garden or a temporary event counter. A spare terminal is only useful if it can connect where it is needed. We'll discuss fixed, portable and mobile [card machines](/card-machines) for those areas.",
          },
          {
            heading: "A clear route from order to payment",
            body:
              "When a pub serves food as well as drinks, the team needs to know where orders go and how they reach the final bill. If you want connected tills, kitchen displays or handheld ordering, check the full workflow in an [EPOS demonstration](/epos-systems), including changes, refunds and separate payments.",
          },
          {
            heading: "Tabs, tips and end-of-shift checks",
            body:
              "Opening a tab, splitting a bill and taking a tip are different functions. Tell us which ones you need and ask to see them on the proposed system. Staff access and reporting matter too: the person closing the shift needs to understand what was sold, what was paid and what still needs attention.",
          },
        ],
      },
      {
        type: "examples",
        heading: "Payment support in working venues",
        items: [
          {
            name: "Cavern Club",
            body: "We support Cavern Club with card machines, handheld EPOS and online payments, covering both in-person and online transactions.",
          },
          {
            name: "Invisible Wind Factory",
            body: "We supplied Invisible Wind Factory with card payments and handheld EPOS, helping the team shorten service times and serve customers more quickly.",
          },
          {
            name: "Breakers Sports Bar",
            body: "We supplied Breakers Sports Bar with an integrated EPOS system, online booking and member management for its pool and sports bar operation.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Compare pub till systems, card machines and costs",
        paragraphs: [
          "A comparison should cover the terminals you need, EPOS software, any integration costs and the contract terms as well as processing rates. Explain your opening hours and ask which support routes are available during them. Do not assume an account-management callback service and an out-of-hours technical service are the same thing.",
          "For a switch, include time to check the layout, train the team and test a normal sale, split bill and refund. Agree the changeover around your trading schedule rather than treating terminal delivery as the whole job.",
        ],
      },
    ],
    faqHeading: "Pub and bar payment questions",
    faqs: [
      {
        q: "Can one system handle bar and table service?",
        a: "That depends on the EPOS and payment combination. Describe both journeys, including where staff take orders and payments, so the proposed setup can be checked against them.",
      },
      {
        q: "Can customers leave a tab open?",
        a: "Tell us how you want tabs to work. An open bill in a till and a card pre-authorisation are different arrangements; we'll need to confirm the features supported by the specific system and account.",
      },
      {
        q: "Will a mobile terminal work in our beer garden?",
        a: "Connectivity needs checking in the actual payment area. Walls, distance and mobile-network coverage can affect the options. Do not choose equipment on the assumption that indoor coverage extends throughout the premises.",
      },
      {
        q: "Can we take tips and split payments?",
        a: "These should be checked on the proposed terminal and EPOS setup. Explain how your team handles them so the demonstration reflects your service, rather than relying on a feature-list heading.",
      },
      {
        q: "How much do card payments and EPOS cost for a pub?",
        a: "The cost depends on your card sales, terminals, software and any connections you need. Compare processing charges, equipment rental, EPOS fees and contract terms together. A recent merchant statement and your existing agreements give us a starting point for a tailored comparison.",
      },
    ],
    related: [
      { label: "Hospitality payments", to: "/industries/hospitality" },
      { label: "Online ordering", to: "/online-ordering" },
      ALL_INDUSTRIES,
    ],
    formHeading: "Talk to us about payments for your pub or bar",
  },

  cafes: {
    breadcrumb: "Cafés",
    seoTitle: "Café & Coffee Shop EPOS and Card Machines | CanDo",
    metaDescription:
      "Card machines and EPOS for cafés and coffee shops. Compare small-payment costs, counter service, food orders and online ordering with help from CanDo.",
    eyebrow: "Cafés and coffee shops",
    h1: "Card machines and EPOS for cafés and coffee shops",
    heroBody:
      "Coffee to go, a table ordering brunch and a customer collecting lunch do not all follow the same path through your café. We help you choose card machines, EPOS and online ordering around the service you offer, with a clear look at the costs of taking lots of smaller payments.",
    img: "https://cdn.candopayments.com/industries/cafes-and-coffee-shops_colour_web_solid.webp",
    sections: [
      {
        type: "cards",
        heading: "Think through the morning rush",
        items: [
          {
            heading: "Keep the counter arrangement practical",
            body:
              "Look at where staff enter an order, where the customer pays and where the order is collected. A countertop terminal may suit a fixed payment point; a portable option may be useful if staff also serve tables. Consider counter space, charging and the connection before choosing a [card machine](/card-machines).",
          },
          {
            heading: "Make food and drink orders clear",
            body:
              "Milk choices, extras and food sent to the kitchen can make an order more involved than a single item on a till. If you need EPOS, ask for a demonstration using your own menu and common changes. Check how the team sees the order and how the payment reaches the bill.",
          },
          {
            heading: "Give collection orders a defined place",
            body:
              "If customers order ahead, decide how those orders will fit alongside people waiting at the counter. Our [online ordering options](/online-ordering) are worth discussing if you want customers to order and pay before collection. Availability, collection times and the connection to your till need checking for the chosen setup.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Compare costs using your actual basket sizes",
        paragraphs: [
          "A percentage rate alone does not tell you what a month of payments will cost. Include any per-transaction charge, terminal rental, software fees and other charges in the comparison. The mix of a single coffee, breakfast for two and larger food orders affects how useful a headline price is to your business.",
          "For an existing café, bring a recent merchant statement. If you are opening, use realistic estimates of turnover and average sale value as a starting point. The calculator provides an indication; your tailored quote should reflect your actual requirements.",
        ],
      },
      {
        type: "prose",
        heading: "Choosing a POS or EPOS system for your coffee shop",
        paragraphs: [
          "POS means point of sale; EPOS is an electronic point-of-sale system. It helps record sales and, depending on the setup, manage menus and orders. The card machine takes the payment. If you want the two to work together, ask us to check the connection before choosing a package.",
          "Start with menu entry, staff access and the reports you need at the end of the day. Then consider extras such as loyalty, kitchen screens or stock tools. Ask which are included in the proposed package and which have separate costs or connections.",
          "For loyalty in particular, check how customers join, how staff apply a reward and whether another service is involved. A demonstration of your intended offer is more useful than assuming every [EPOS system](/epos-systems) includes the same loyalty tools.",
        ],
      },
    ],
    faqHeading: "Café payment questions",
    faqs: [
      {
        q: "Do I need EPOS if I only have a small café?",
        a: "A card machine and an EPOS system do different jobs. If you need help managing menus, orders or sales reporting, EPOS may be useful. If you already have a till that works for you, start by checking what you actually need to change.",
      },
      {
        q: "How much does a café card machine cost?",
        a: "Compare the equipment cost with processing charges, software and any connection fees. Your average sale value matters: a per-transaction charge affects lots of small coffee purchases differently from fewer large bills. Bring a statement, or your expected turnover and basket size, so we can prepare a relevant quote.",
      },
      {
        q: "Can I take payments at tables as well as the counter?",
        a: "We can discuss portable terminals and the connectivity your premises needs. Connecting table payments to an existing till requires a separate compatibility check.",
      },
      {
        q: "Can customers order breakfast or lunch for collection?",
        a: "Online ordering is one option. We'll need to understand your menu, collection process and EPOS before confirming the setup and any supported connections.",
      },
      {
        q: "Is loyalty included with every EPOS package?",
        a: "No single feature list applies to every package. Tell us the loyalty offer you want and ask for its availability, cost and operation to be confirmed in the proposal.",
      },
    ],
    related: [
      { label: "Restaurant and hospitality payments", to: "/industries/hospitality" },
      { label: "Takeaway payments", to: "/industries/takeaways" },
      ALL_INDUSTRIES,
    ],
    formHeading: "Talk to us about payments for your café",
  },

  takeaways: {
    breadcrumb: "Takeaways",
    seoTitle: "Takeaway EPOS, Card Payments & Online Ordering | CanDo",
    metaDescription:
      "Payment options for takeaway counters, phone orders and online collection. Plan card machines, EPOS and online ordering around your kitchen and team.",
    eyebrow: "Takeaways and food-to-go",
    h1: "Takeaway EPOS, card payments and online ordering",
    heroBody:
      "Walk-in customers, phone orders and online orders can all arrive during the same busy period. We help takeaways choose card machines, EPOS and online ordering with a clear process for when customers pay and how the team checks each order.",
    img: "https://cdn.candopayments.com/industries/takeaways-and-food-to-go_colour_web_solid.webp",
    sections: [
      {
        type: "cards",
        heading: "Give each ordering route a clear payment step",
        items: [
          {
            heading: "At the counter",
            body:
              "Choose a payment point that works with ordering and collection. If staff move between counters or take payments away from the till, discuss portable or mobile terminals as well as fixed [card machines](/card-machines). Connection and charging arrangements should match where they will be used.",
          },
          {
            heading: "On your own ordering page",
            body:
              "[Online ordering](/online-ordering) lets customers choose and pay before collection or delivery. Start with the information the kitchen needs: items, options, availability and the agreed collection or delivery process. Confirm how paid orders are identified and how the chosen system connects to your EPOS or other order records.",
          },
          {
            heading: "After a phone order",
            body:
              "If someone orders by phone, decide whether they will pay at collection or remotely. A payment link and a [virtual terminal](/virtual-terminal) are different options. We can explain the payment routes and discuss which fits your staff process and account requirements.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Compare the full cost of online orders",
        paragraphs: [
          "An ordering service, card processing and delivery are separate parts of the cost. Ask for the setup fees, recurring software charges, transaction fees and any commission to be shown clearly. If you also use a delivery marketplace, keep its fees and services in the comparison.",
          "Having your own ordering route does not make payment processing free. It also does not automatically replace the discovery or delivery service a marketplace provides. A useful choice starts with what you want your own channel to do and what it will cost to run.",
        ],
      },
      {
        type: "prose",
        heading: "Choosing a takeaway POS system",
        paragraphs: [
          "A takeaway point-of-sale system needs to fit how your orders arrive, reach the kitchen and get collected or delivered. Start with the parts you need to connect. Counter payments, your own online ordering and delivery-marketplace orders may need different products or integrations; ask for each connection and its cost to be confirmed.",
        ],
      },
      {
        type: "prose",
        heading: "Check the kitchen workflow before you switch",
        paragraphs: [
          "Run through a busy evening in the demonstration. How does a phone order reach the kitchen? Can staff see which orders have been paid? What happens when an item sells out, a customer changes an order or a refund is needed?",
          "If you use printers, kitchen screens or third-party delivery services, list each one. Integrations must be checked for the exact product and version. Driver dispatch, delivery messages and marketplace connections should only appear in your proposal if the selected system supports them and their costs are clear.",
        ],
      },
    ],
    faqHeading: "Takeaway payment questions",
    faqs: [
      {
        q: "Can I keep using delivery marketplaces?",
        a: "Tell us which services you use and what you need to connect. A separate ordering route may sit alongside them, but a direct integration must be confirmed for your chosen system.",
      },
      {
        q: "Is an online ordering system the same as a payment gateway?",
        a: "No. The ordering system handles the menu and order journey; the payment service handles the payment step. Your proposal should explain both and the charges associated with each.",
      },
      {
        q: "Can customers pay before they collect?",
        a: "Online ordering and payment links are options for taking payment remotely. Which route makes sense depends on how the order is placed and how your team records it.",
      },
      {
        q: "Can drivers take payment on delivery?",
        a: "We can discuss mobile-terminal requirements, including the areas covered and the connection available. Do not assume every terminal or delivery integration supports the same process.",
      },
      {
        q: "Will my existing kitchen printer work?",
        a: "We'll need its model and your current EPOS details before confirming compatibility. Include every printer or screen you want to retain when requesting a proposal.",
      },
    ],
    related: [
      { label: "Café payments", to: "/industries/cafes" },
      { label: "Hospitality payments", to: "/industries/hospitality" },
      { label: "EPOS systems", to: "/epos-systems" },
      ALL_INDUSTRIES,
    ],
    formHeading: "Talk to us about payments and ordering for your takeaway",
  },

  retail: {
    breadcrumb: "Retail",
    seoTitle: "Card Machines & EPOS for Retail Shops | CanDo Payments",
    metaDescription:
      "Card machines and EPOS for independent shops and retailers. Explore checkout, stock and online sales requirements, with a review of your payment costs.",
    eyebrow: "Retail shops and stores",
    h1: "Card machines, EPOS and payment solutions for retailers",
    heroBody:
      "Your payment setup has to fit the shop you run: a single till, several departments, a pop-up or a shop that also sells online. We help retailers choose card machines, EPOS and online payment options, with attention to checkout, stock and the costs behind each sale.",
    img: "https://cdn.candopayments.com/industries/retail_colour_web_solid.webp",
    sections: [
      {
        type: "cards",
        heading: "Start at the till, then look beyond it",
        items: [
          {
            heading: "A payment point that fits your counter",
            body:
              "Consider where customers queue, where purchases are packed and whether the team ever needs to take payment elsewhere. We can discuss countertop, portable and mobile [card machines](/card-machines). If you want the till to send the amount to the terminal, compatibility needs checking for the specific combination.",
          },
          {
            heading: "Choosing a retail POS system",
            body: [
              "A retail point-of-sale system, also called POS or EPOS, records the sale and may support stock and product information. Start with the jobs your shop needs it to do, then check the features and payment connections in the proposed package.",
              "For clothing, gifts or other varied stock, an EPOS demonstration should use realistic products: sizes, colours, barcodes and returns. Check what staff can see and change, and how you get the sales reports you need. The right [retail EPOS](/epos-systems) depends on those everyday jobs, not simply the length of its feature list.",
            ],
          },
          {
            heading: "In-store and online sales",
            body:
              "An online payment gateway does not, by itself, synchronise your website stock with your shop. Explain your website platform and stock system so the connections can be assessed. If click and collect matters, include reserving the item, taking payment, recording collection and handling a return in the proposed workflow.",
          },
          {
            heading: "Convenience stores and smaller purchases",
            body:
              "If you take many small payments, compare both the percentage rate and any charge per transaction. For the till demonstration, include scanning a basket, correcting an item and checking the amount on the terminal. Tell us which barcode, stock and payment functions you already use before replacing equipment.",
          },
        ],
      },
      {
        type: "examples",
        heading: "Retailers we have helped",
        items: [
          {
            name: "The 1755 Collection",
            body: "We helped antiques retailer The 1755 Collection take payments in-store and online, with card payments, EPOS and ecommerce payments on its website.",
          },
          {
            name: "Kurt Muller",
            body: "We helped national wholesale retailer Kurt Muller reduce its payment-processing costs with card payments and payment links. After a short time with CanDo, we helped secure further savings, continuing our support beyond the initial setup.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Compare a complete month of trading",
        paragraphs: [
          "Bring a recent merchant statement and any separate terminal or EPOS bills. Check the processing charges, equipment rental, software costs, contract length and the terms for leaving the current provider. A lower headline percentage may not describe the whole cost of changing your setup.",
          "If you have more than one site, say which reports need to be shared and which staff need access. Central stock, reporting and payment settlement are separate requirements. Each should be confirmed in the proposal rather than treated as one automatic multi-site feature.",
        ],
      },
    ],
    faqHeading: "Retail payment questions",
    faqs: [
      {
        q: "Can I keep my current till?",
        a: "We'll check its payment options before recommending a change. Some businesses need a different terminal; others need a broader EPOS update. Tell us what you use and which parts are working well.",
      },
      {
        q: "Will my shop and website share the same stock figures?",
        a: "Only if the chosen systems support the required connection. We'll need details of your website, EPOS and stock tools. Payment processing and stock synchronisation should be checked separately.",
      },
      {
        q: "Can you help with pop-ups or temporary selling spaces?",
        a: "We can discuss mobile-terminal options and the connection available at the location. Tell us how often you trade there and what equipment or contract terms you need.",
      },
      {
        q: "What should we test in an EPOS demonstration?",
        a: "Use a normal sale, a product with variations, a refund and an end-of-day report. Add stock transfers or click and collect if those are part of your business.",
      },
      {
        q: "Can I take payments online as well as in the shop?",
        a: "Yes, we can discuss both payment channels. Our [e-commerce page](/industries/e-commerce) explains the checkout and platform questions to consider alongside your in-store setup.",
      },
    ],
    related: [
      { label: "Online payments", to: "/online-payments" },
      { label: "E-commerce payments", to: "/industries/e-commerce" },
      ALL_INDUSTRIES,
    ],
    formHeading: "Talk to us about payments for your shop",
  },

  "personal-care": {
    breadcrumb: "Personal Care",
    seoTitle: "Card Payments for Clinics & Therapists | CanDo Payments",
    metaDescription:
      "Payment options for clinics, therapists and appointment services. Explore reception payments, deposits and mobile working with practical setup guidance.",
    eyebrow: "Personal care and appointment services",
    h1: "Card payments for clinics, therapists and appointment services",
    heroBody:
      "An appointment may be booked by phone, paid for at reception or followed by another visit. We help appointment-led businesses look at card machines and remote payments around that process, with clear questions about deposits, booking software and who needs access.",
    img: "https://cdn.candopayments.com/industries/personal-care-services_colour_web_solid.webp",
    sections: [
      {
        type: "cards",
        heading: "Separate the booking from the payment",
        items: [
          {
            heading: "At reception",
            body:
              "If clients pay after an appointment, a fixed payment point may suit the reception team. If you work between rooms, discuss a portable option and its connection. A [card machine](/card-machines) takes the payment; it does not automatically update the appointment or client record in another system.",
          },
          {
            heading: "Before the appointment",
            body:
              "A payment link can be an option when you ask for an advance payment. Decide how your team will identify the booking, record the amount paid and check any remaining balance. We can discuss [remote payment options](/online-payments) alongside that process.",
          },
          {
            heading: "Away from your premises",
            body:
              "For home visits or mobile working, tell us where you operate, how often you take payment and what connection is available. Include the practical details: carrying the terminal, keeping it charged and recording the payment against the right appointment.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Card payments alongside your clinic or booking software",
        paragraphs: [
          "Your booking or practice-management software may already be central to the working day. Before changing payments, identify which functions it provides and whether its payment connections can be changed. We'll need the product and version before confirming an integration.",
          "Payment links, automatic reminders and diary updates are separate capabilities. If you want them connected, ask for a demonstration of the complete booking-to-payment journey. If you intend to keep them separate, make the staff process clear so a completed payment does not leave an appointment record out of date.",
        ],
      },
      {
        type: "prose",
        heading: "Compare reception and remote-payment costs",
        paragraphs: [
          "List the payments you take at reception separately from deposits or balances paid remotely. Ask for the equipment, processing and software charges for each route. If you already have a provider, a merchant statement helps with the comparison; patient or treatment records are not needed.",
        ],
      },
      {
        type: "prose",
        heading: "Explain the business activity when you enquire",
        paragraphs: [
          '"Personal care" covers very different businesses. Tell us the actual services you provide, how and when clients pay, and whether you take advance or repeat payments. Product suitability and account approval depend on the business and proposed use.',
          "For hairdressing, barbering, nails or everyday beauty services, our [beauty and hair page](/industries/beauty) covers the salon workflow in more detail. This page focuses on reception-led and appointment-based services; it does not assume every clinic, treatment or payment arrangement is eligible.",
        ],
      },
    ],
    faqHeading: "Appointment-service payment questions",
    faqs: [
      {
        q: "Can I take a booking deposit remotely?",
        a: "Payment links are one option to discuss. You will need a clear way to identify the appointment and the amount paid. Taking a payment does not, by itself, update a separate booking system.",
      },
      {
        q: "Does a card terminal replace my booking software?",
        a: "No. It handles the payment function. If you also need booking or reporting tools, describe those requirements separately so the proposed products and connections can be checked.",
      },
      {
        q: "Can the payment system send appointment reminders?",
        a: "Do not assume that feature is included. Tell us which booking system you use and what you want the reminder to do; any supported connection and separate cost should be confirmed.",
      },
      {
        q: "Can I accept payments during home visits?",
        a: "We can discuss mobile payment requirements. Suitability depends on your activity, account approval and the connection available where you work.",
      },
      {
        q: "What information should I send with an enquiry?",
        a: "Describe your business activity, payment needs and software. Please do not include patient, client or treatment records in a general payment enquiry.",
      },
    ],
    related: [
      { label: "Beauty and hair payments", to: "/industries/beauty" },
      { label: "Phone payments", to: "/virtual-terminal" },
      ALL_INDUSTRIES,
    ],
    formHeading: "Talk to us about payments for your appointment business",
  },

  beauty: {
    breadcrumb: "Beauty & Hair",
    seoTitle: "Card Machines for Salons, Hairdressers & Barbers | CanDo",
    metaDescription:
      "Card machines and payment options for hair salons, barbers and beauty businesses. Plan salon checkout, deposits, product sales and mobile appointments.",
    eyebrow: "Hair, beauty and barbering",
    h1: "Card machines for salons, hairdressers and barbers",
    heroBody:
      "A client might pay a deposit when booking, add a product after their appointment and book their next visit before leaving. We help salons, barbers and beauty professionals choose payment options around that journey, whether you work at a fixed reception or visit clients.",
    img: "https://cdn.candopayments.com/industries/beauty-therapists-and-hair-salons_colour_web_solid.webp",
    sections: [
      {
        type: "cards",
        heading: "Fit payments around the appointment",
        items: [
          {
            heading: "At the salon checkout",
            body:
              "Consider who takes payment and where. A countertop terminal can suit a reception desk; portable equipment may be useful where staff move between clients. If a treatment and product are sold together, decide how the till records the items and how the final amount reaches the [card machine](/card-machines).",
          },
          {
            heading: "When taking a booking payment",
            body:
              "If you request a deposit, a payment link is one remote-payment option. Your team still needs to know which appointment it relates to and how much remains to be paid. Discuss the connection to your diary, or the separate process staff will use to keep the records aligned.",
          },
          {
            heading: "At a client's home or an event",
            body:
              "For mobile hair and beauty work, plan the payment step before you arrive. Tell us the areas you cover and what you need from the terminal. Connection, battery use and account suitability need checking; a mobile machine is not a promise of coverage everywhere.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Merchant services for salons and beauty businesses",
        paragraphs: [
          "Merchant services are the services used to accept and process customer card payments. For a salon, compare those charges alongside the terminal, booking software and any payment connections. A lower processing rate may not tell you the whole cost if another part of the setup has a separate fee.",
        ],
      },
      {
        type: "prose",
        heading: "Choosing a salon payment system",
        paragraphs: [
          "You may already have booking software your clients and team know well. Bring its name and payment arrangements to the conversation before deciding to replace anything. Ask which payment connections are supported, whether there are additional fees and what changes for staff.",
          "If you are considering [EPOS](/epos-systems), use a realistic demonstration: a service, a retail product, a deposit applied to the balance and a refund. Add tips, gift vouchers or loyalty only if you need them, and ask for each function to be confirmed on the proposed package.",
        ],
      },
      {
        type: "prose",
        heading: "One salon can contain several businesses",
        paragraphs: [
          "If independent professionals rent chairs or space, explain who is selling the service and who should receive each payment. That needs to be understood before the payment-account structure is chosen. Do not assume one terminal can automatically divide takings between independent businesses.",
          "We can discuss your payment requirements and the options available for the proposed arrangement. The account setup and any software connections need confirmation before you commit.",
        ],
      },
    ],
    faqHeading: "Salon and beauty payment questions",
    faqs: [
      {
        q: "Can I take deposits without changing my booking system?",
        a: "Possibly. A separate payment link may fit your process, but your team will need to record the payment against the right appointment. If you want automatic updates, the booking-system integration must be checked.",
      },
      {
        q: "Can I use the same setup for services and retail products?",
        a: "Tell us how you want those sales recorded. A terminal takes payment, while an EPOS system may provide the product and service records. The proposed combination should be demonstrated using both.",
      },
      {
        q: "Will deposits prevent missed appointments?",
        a: "No payment system can guarantee that. A deposit gives you a way to take an agreed advance payment; booking terms, reminders and client communication are separate parts of your process.",
      },
      {
        q: "Can different chair renters use one payment account?",
        a: "Explain the business arrangement before choosing equipment. Who supplies the service and receives the funds matters; a suitable account structure must be confirmed rather than assumed.",
      },
      {
        q: "Can you help a mobile hairdresser or beauty professional?",
        a: "We can discuss mobile and remote payment options based on your activity, working locations and approval requirements.",
      },
    ],
    related: [
      { label: "Remote payments and payment links", to: "/online-payments" },
      { label: "Clinics and appointment services", to: "/industries/personal-care" },
      ALL_INDUSTRIES,
    ],
    formHeading: "Talk to us about payments for your salon or beauty business",
  },

  "e-commerce": {
    breadcrumb: "E-Commerce",
    seoTitle: "E-commerce Payments & Gateway Options | CanDo Payments",
    metaDescription:
      "Compare online payments for your e-commerce business. Review processing costs, checkout requirements and gateway compatibility before changing provider.",
    eyebrow: "Online shops and e-commerce",
    h1: "E-commerce payments and gateway options for online shops",
    heroBody:
      "Your payment provider has to work with your website, your checkout and the way your team manages orders. We help online businesses review processing costs and payment-gateway options, with the platform and integration checks made before a change is agreed.",
    img: "https://cdn.candopayments.com/industries/e-commerce_colour_web_solid.webp",
    choice: ECOM_CHOICE_BLOCK,
    proof: ECOM_PROOF_STRIP,
    finalCta: ECOM_FINAL_CTA,
    sections: [
      {
        type: "cards",
        heading: "Look at the whole checkout journey",
        items: [
          {
            heading: "The website and the gateway",
            body:
              "Tell us your platform, its version and any checkout extensions you rely on. Hosted payment pages, plug-ins and custom integrations have different requirements. We'll discuss the [payment gateway](/payment-gateway) route available for your setup rather than assume every website can use the same connection.",
          },
          {
            heading: "How customers want to pay",
            body:
              "Include the card types, wallets, countries and currencies you need in the brief. Their availability depends on the payment product, integration and account configuration. Check what the customer will see on a phone as well as on a desktop, including any authentication step and the return to the order confirmation.",
          },
          {
            heading: "What happens after payment",
            body:
              "The team needs to identify a paid order, handle a failed attempt and process a refund when required. Agree which records live in the website and which live in the payment dashboard. If subscriptions are involved, include repeat billing and how changes or failures are handled in the assessment.",
          },
        ],
      },
      {
        type: "examples",
        heading: "Online businesses we have helped",
        items: [
          {
            name: "AD Teamwear",
            body: "We moved AD Teamwear's WooCommerce payments from Stripe to the CanDo payment gateway, reducing card-processing costs and arranging next-day settlement for the business.",
          },
          {
            name: "Lymm Engine Components",
            body: "We integrated a payment gateway with Lymm Engine Components' OpenCart website and supported payment links and telephone payments. The setup reduced processing costs and improved payment-processing efficiency, with funds reaching the bank the next day.",
          },
        ],
        note: "These examples describe those businesses' setups. We'll check the platform, account and settlement arrangements for your own proposal.",
      },
      {
        type: "prose",
        heading: "Compare the costs you actually pay",
        paragraphs: [
          "A useful review includes processing and gateway charges, any per-transaction fees, recurring costs and the card mix behind your sales. If you sell internationally or take subscriptions, include those requirements too. A recent merchant statement helps us start with your existing costs rather than a headline rate alone.",
          "For a new online business, the [online payment estimate](/quote/online) gives an indication based on the information entered. It is not a formal quote or confirmation that a particular integration is available.",
        ],
      },
      {
        type: "prose",
        heading: "Comparing payments for WooCommerce or OpenCart",
        paragraphs: [
          "Our customer examples include both platforms. If you are comparing providers for your own shop, bring the platform version, checkout extensions and any subscription requirements. Ask which integration is proposed, who will maintain it and what needs testing before the switch. A supported setup for one shop does not confirm every extension or version.",
        ],
      },
      {
        type: "prose",
        heading: "Plan the change with your website team",
        paragraphs: [
          "Ask your developer to be part of the integration discussion. Before switching, agree the test plan for successful payments, failures, refunds and order confirmation. Include subscriptions or saved payment arrangements where relevant; they must not be assumed to transfer automatically.",
          "If you also take payments in a physical shop, our [retail page](/industries/retail) covers the till and stock questions. Connecting channels needs its own checks—it is more than installing a gateway.",
        ],
      },
    ],
    faqHeading: "E-commerce payment questions",
    faqs: [
      {
        q: "Can I keep my existing website?",
        a: "That is the starting point for the assessment. We'll need your platform, version and checkout requirements to confirm an available integration and the work involved.",
      },
      {
        q: "Do you work with WooCommerce or OpenCart?",
        a: "Our customer examples above include both. That is evidence of particular implementations, not a guarantee for every version, extension or business model. We'll check your setup before recommending a route.",
      },
      {
        q: "Can I offer Apple Pay or Google Pay?",
        a: "Tell us which wallets you want to offer. Support needs confirming for the selected gateway, integration and account before it is included in your proposal.",
      },
      {
        q: "Can my subscriptions move to a new provider?",
        a: "Do not assume they can move unchanged. The current billing system and stored-payment arrangements need assessment before a migration plan is agreed.",
      },
      {
        q: "Will a new gateway remove all fraud or compliance work?",
        a: "No. A payment product's security features do not eliminate every risk or every responsibility of the business using it. Ask for the applicable setup and operating requirements to be explained for your integration.",
      },
    ],
    related: [
      { label: "Online payment options", to: "/online-payments" },
      { label: "Payment gateway", to: "/payment-gateway" },
      { label: "Retail payments", to: "/industries/retail" },
      ALL_INDUSTRIES,
    ],
    formHeading: "Talk to us about your website payment setup",
  },

  fitness: {
    breadcrumb: "Fitness",
    seoTitle: "Gym Payments, Memberships & Fitness Studios | CanDo",
    metaDescription:
      "Payment options for gyms, studios and personal trainers. Explore memberships, classes, reception sales and the connections your booking setup needs.",
    eyebrow: "Gyms, studios and personal trainers",
    h1: "Gym payments for memberships, classes and reception",
    heroBody:
      "A membership, a single class and a drink bought at reception are different payment journeys. We help gyms, studios and personal trainers look at card machines, online payments and the connections their booking or membership setup needs.",
    img: "https://cdn.candopayments.com/industries/fitness-training-services_colour_web_solid.webp",
    sections: [
      {
        type: "cards",
        heading: "Start with what members buy",
        items: [
          {
            heading: "Memberships and repeat payments",
            body:
              "Explain how members join, how often they pay and which system holds the membership record. Recurring card payments and other collection methods are not interchangeable. If you want to change provider, the existing billing arrangements need assessing before you assume they can move.",
          },
          {
            heading: "Classes, courses and personal training",
            body:
              "A one-off class, a block of sessions and a deposit can each need a different record. Our [online payment options](/online-payments) include routes to discuss for remote payments. Check how a completed payment is connected to the booking and who handles a change or refund.",
          },
          {
            heading: "Reception and shop sales",
            body:
              "For day passes, equipment or refreshments, discuss where people pay and whether the sale needs to be recorded through [EPOS](/epos-systems). A terminal at reception does not automatically update a member's access or class booking; those connections must be checked separately.",
          },
        ],
      },
      {
        type: "examples",
        heading: "Experience in fitness and sport",
        items: [
          {
            name: "Apex Wirral",
            body: "We support local private gym Apex Wirral with EPOS and subscription payments as part of its class-management setup.",
          },
          {
            name: "Velocity Padel",
            body: "We support Velocity Padel's clubs in Wigan and Manchester with integrated EPOS and card payments, covering retail sales, court bookings and online bookings.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Bring the membership system into the discussion",
        paragraphs: [
          "Before selecting equipment, list your booking platform, membership software and any access-control system. Decide what each system should do and which connections are essential. A useful demonstration should follow a new customer through booking, payment and the record your team sees afterwards.",
          "Also check the exceptions: a failed repeat payment, a cancelled class, a frozen membership or a refund. Ask who owns each step and what the selected payment service actually supports. Software, integration and processing charges should be clear in the proposal.",
          "For personal trainers working across venues, include the connection available at each location and whether clients usually pay in person or before a session. You may need a different arrangement from a staffed reception desk.",
        ],
      },
    ],
    faqHeading: "Fitness payment questions",
    faqs: [
      {
        q: "What recurring payment options work for gyms and studios?",
        a: "Start with how you collect memberships today and which system holds the member record. Our work with Apex Wirral includes subscription payments. We'll assess your billing model, membership software and account requirements before confirming a suitable setup. Moving existing recurring payments needs a separate check; it is not an automatic part of changing card machines.",
      },
      {
        q: "Can I take payment for one-off classes as well as memberships?",
        a: "We can discuss both. The important check is how each payment connects to the right booking or membership record, particularly if more than one system is involved.",
      },
      {
        q: "Will payment automatically open a gym door or turnstile?",
        a: "Do not assume it will. Access control and payments are separate systems unless a supported connection has been confirmed and tested.",
      },
      {
        q: "Can a personal trainer take payment away from a reception desk?",
        a: "Mobile and remote options are worth discussing. Tell us where you work, what clients pay for and when payment is due so the requirements can be assessed.",
      },
      {
        q: "Can we keep our current booking platform?",
        a: "We'll start by checking it. Provide the product, version and the payment functions you use today. A compatible route needs confirming before a change is planned.",
      },
    ],
    related: [
      { label: "Card machines", to: "/card-machines" },
      { label: "Online payments", to: "/online-payments" },
      { label: "Retail payments", to: "/industries/retail" },
      ALL_INDUSTRIES,
    ],
    formHeading: "Talk to us about payments for your fitness business",
  },

  "pet-services": {
    breadcrumb: "Pet Services",
    seoTitle: "Card Machines for Dog Groomers & Pet Services | CanDo",
    metaDescription:
      "Card machines and remote payments for groomers, boarding businesses, vets and pet shops. Plan deposits, collection payments and mobile visits with CanDo.",
    eyebrow: "Grooming, boarding and pet businesses",
    h1: "Card machines for dog groomers and pet-service businesses",
    heroBody:
      "Payment might happen when a pet is collected, when a booking is made or during a visit to the customer. We help pet businesses compare card machines and remote payment options around those different moments, whether you run a grooming salon, boarding business, veterinary practice or shop.",
    img: "https://cdn.candopayments.com/industries/pet-services_colour_web_solid.webp",
    sections: [
      {
        type: "cards",
        heading: "Fit the payment step around the booking",
        items: [
          {
            heading: "When the customer collects their pet",
            body:
              "A fixed counter may work for a grooming salon or practice reception. If staff need to move between payment areas, discuss portable equipment and its connection. Choose a [card machine](/card-machines) around the actual collection process, including where customers wait and where the terminal can charge.",
          },
          {
            heading: "When a future booking is made",
            body:
              "If you request advance payment for boarding or an appointment, a payment link is one option. Your team needs a way to match the payment to the booking and record any balance. [Remote payment options](/online-payments) can be discussed without assuming a new booking system is required.",
          },
          {
            heading: "At the customer's location",
            body:
              "Mobile groomers and visiting services need to consider coverage where they work. Explain whether customers pay during the visit or later, and whether a mobile terminal or remote payment route would better fit the process. The account and connection requirements need checking before a setup is confirmed.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Payment processing for pet-service businesses",
        paragraphs: [
          "Compare the charges for payments at collection with any remote-payment costs for deposits. Include equipment, software and connection fees, and tell us about seasonal changes in bookings. If you sell food or accessories too, include those purchases when comparing your typical sale values.",
        ],
      },
      {
        type: "prose",
        heading: "Products, services and repeat customers",
        paragraphs: [
          "A business that sells food or accessories as well as services may want both types of sale in its till records. If you need stock tools, use an [EPOS demonstration](/epos-systems) to check how retail items and service payments are handled.",
          "Repeat custom does not always require automatic billing. First decide what changes from one visit to the next, when the price is confirmed and how you obtain agreement to pay. If recurring payments are needed, discuss that specific use with us rather than assuming they are included with a card machine.",
        ],
      },
      {
        type: "prose",
        heading: "Work with the software you already have",
        paragraphs: [
          "Tell us which booking or practice-management system your team uses and how payments are recorded today. A terminal can take a payment without being integrated with that software; automatic updates require a supported connection.",
          "Include deposits, changes and refunds in the discussion. For a veterinary practice, keep the payment brief focused on the collection process and required software connection. Do not assume payment equipment also provides clinical, insurance or practice-management functions.",
        ],
      },
    ],
    faqHeading: "Pet-business payment questions",
    faqs: [
      {
        q: "Can I take a boarding or grooming deposit remotely?",
        a: "Payment links are an option to discuss. Explain how you record the booking and any remaining balance so the payment process can be considered alongside it.",
      },
      {
        q: "Will the terminal connect to my booking software?",
        a: "That depends on the software and payment product. We'll need the name, version and intended workflow before confirming a connection. A standalone terminal is different from an integrated booking setup.",
      },
      {
        q: "Can I take payment during mobile grooming visits?",
        a: "We can discuss mobile equipment and remote payments. The right route depends on where you work, available connectivity and account suitability.",
      },
      {
        q: "Can I sell pet products and services through the same till?",
        a: "Tell us how you need those sales recorded and whether you need stock reporting. The proposed EPOS should be demonstrated with both product and service transactions.",
      },
      {
        q: "Do you provide veterinary billing or insurance software?",
        a: "This page covers payment services. If you need a connection to specialist software, describe it separately so compatibility and ownership can be checked before any commitment.",
      },
    ],
    related: [
      { label: "Retail shop payments", to: "/industries/retail" },
      { label: "Online payments", to: "/online-payments" },
      ALL_INDUSTRIES,
    ],
    formHeading: "Talk to us about payments for your pet business",
  },
};
